import React, {useState} from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Collapse,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {TrendingUp, TrendingDown, CheckCircle, Warning, Error, Info, Timeline, Assessment, ExpandMore, ExpandLess} from "@mui/icons-material";

interface PondPerformanceAnalyticsProps {
  aiAnalysis?: any;
  loading?: boolean;
  performanceMetrics?: any;
  cycleId?: string;
}

const EnhancedPondAnalytics: React.FC<PondPerformanceAnalyticsProps> = ({aiAnalysis, loading = false, performanceMetrics, cycleId}) => {
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);
  const {t} = useTranslation();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!performanceMetrics) {
    return <Alert severity="info">Performance metrics not available. Please ensure you have feed records and sampling data.</Alert>;
  }

  const isHarvestMetrics = performanceMetrics.metricsSource === "harvest";
  const harvestedBiomass = Number(performanceMetrics.biomassRange?.exact ?? performanceMetrics.totalBiomassProduced) || 0;

  const getPerformanceIcon = (level: string) => {
    switch (level) {
      case "excellent":
        return <CheckCircle color="success" />;
      case "good":
        return <CheckCircle color="primary" />;
      case "average":
        return <Warning color="warning" />;
      case "poor":
        return <Error color="error" />;
      default:
        return <Info color="info" />;
    }
  };

  const getPerformanceColor = (level: string) => {
    switch (level) {
      case "excellent":
        return "success";
      case "good":
        return "primary";
      case "average":
        return "warning";
      case "poor":
        return "error";
      default:
        return "default";
    }
  };

  const getTrendIcon = (trend: string) => {
    return trend === "increasing" ? <TrendingUp color="warning" /> : <TrendingDown color="success" />;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight={600} color="primary">
        {t("Pond Performance Analytics")}
      </Typography>

      <Grid container spacing={3}>
        {/* Key Metrics Analysis Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("Key Metrics Analysis")}
              </Typography>

              {performanceMetrics && (
                <Box>
                  {/* Survival Analysis */}
                  <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight={600} color="primary">
                      {t("Survival Rate")}
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {performanceMetrics.survivalRateRange ? `${performanceMetrics.survivalRateRange.exact.toFixed(1)}%` : "N/A"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Range:{" "}
                      {performanceMetrics.survivalRateRange
                        ? `${performanceMetrics.survivalRateRange.min.toFixed(1)}-${performanceMetrics.survivalRateRange.max.toFixed(1)}%`
                        : "N/A"}
                    </Typography>
                  </Box>

                  {/* ABW Analysis */}
                  <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight={600} color="primary">
                      {t("Average Body Weight")}
                    </Typography>
                    <Typography variant="h5" color="info.main">
                      {performanceMetrics.abw ? `${Number(performanceMetrics.abw).toFixed(2)} g` : "N/A"}
                    </Typography>
                  </Box>

                  {/* FCR Analysis */}
                  <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight={600} color="primary">
                      {t("Feed Conversion Ratio (FCR)")}
                    </Typography>
                    <Typography variant="h5" color="warning.main">
                      {performanceMetrics.fcr ? `${Number(performanceMetrics.fcr).toFixed(2)}` : "N/A"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {isHarvestMetrics ? t("From completed harvest") : t("Calculated from AI survival prediction")}
                    </Typography>
                  </Box>

                  {/* Age */}
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t("Age")}: {performanceMetrics.age || 0} {t("days")}
                    </Typography>
                  </Box>

                  {/* Biomass Analysis */}
                  <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight={600} color="primary">
                      {t("Biomass")}
                    </Typography>
                    <Typography variant="h5" color="warning.main">
                      {isHarvestMetrics
                        ? `${harvestedBiomass.toFixed(1)} kg`
                        : performanceMetrics.biomassRange
                          ? `${Number(performanceMetrics.biomassRange.exact).toFixed(1)} kg`
                          : "N/A"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {isHarvestMetrics
                        ? t("Harvested biomass")
                        : performanceMetrics.biomassRange
                          ? `${t("Range")}: ${Number(performanceMetrics.biomassRange.min).toFixed(1)}-${Number(performanceMetrics.biomassRange.max).toFixed(1)} kg`
                          : "N/A"}
                    </Typography>
                  </Box>

                  {/* Feed Analysis */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="primary">
                      {t("Total Feed Consumed")}
                    </Typography>
                    <Typography variant="h5" color="secondary.main">
                      {performanceMetrics.totalFeed ? `${Number(performanceMetrics.totalFeed).toFixed(1)} kg` : "N/A"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      FCR: {performanceMetrics.fcr ? Number(performanceMetrics.fcr).toFixed(2) : "N/A"}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* FCR Calculation Method Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("FCR Calculation Method")}
              </Typography>

              {isHarvestMetrics ? (
                <>
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    {t("FCR is calculated from the completed harvest")}: <strong>FCR = {t("Total Feed")} / {t("Harvested biomass")}</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("Harvested biomass is the total weight recorded on harvest, and survival is harvested count divided by stocked seed.")}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    FCR is now calculated using the formula: <strong>FCR = Total Feed / AI_Predicted_Biomass</strong>
                  </Typography>

                  <Typography variant="body2" color="textSecondary" mb={1}>
                    Where AI_Predicted_Biomass = (AI_Survival_Prediction × Total_Seed × ABW) / 1000 + Harvested_Weight
                  </Typography>

                  <Typography variant="body2" color="textSecondary">
                    This provides more accurate and predictive performance metrics based on AI survival analysis.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* AI Analysis Card */}
        {aiAnalysis && !isHarvestMetrics && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6">AI-Powered Pond Analysis</Typography>
                    {aiAnalysis.enabled ? (
                      <Box display="flex" gap={1}>
                        <Chip label="AI Analysis" color="primary" size="small" />
                      </Box>
                    ) : (
                      <Chip label="Not Available" color="default" size="small" />
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowAIAnalysis(!showAIAnalysis)}
                    startIcon={showAIAnalysis ? <ExpandLess /> : <ExpandMore />}
                    size="small">
                    {showAIAnalysis ? "Hide AI Analysis" : "Show AI Analysis"}
                  </Button>
                </Box>

                <Collapse in={showAIAnalysis}>
                  {aiAnalysis.enabled ? (
                    <Box>
                      {/* Current Pond State Analysis */}
                      <Box mb={3}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom color="primary">
                          🔍 Current Pond State Analysis
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                          {aiAnalysis.analysis || "AI analysis of current pond conditions and performance."}
                        </Typography>
                      </Box>

                      {/* Key Insights */}
                      {aiAnalysis.insights && aiAnalysis.insights.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom color="info.main">
                            💡 Key Insights for Farmer
                          </Typography>
                          <List dense>
                            {aiAnalysis.insights.map((insight: string, index: number) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <Info color="info" />
                                </ListItemIcon>
                                <ListItemText primary={insight} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Actionable Recommendations */}
                      {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom color="success.main">
                            🎯 Actionable Recommendations
                          </Typography>
                          <List dense>
                            {aiAnalysis.recommendations.map((recommendation: string, index: number) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <CheckCircle color="success" />
                                </ListItemIcon>
                                <ListItemText primary={recommendation} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Risk Assessment */}
                      {aiAnalysis.risks && aiAnalysis.risks.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom color="error.main">
                            ⚠️ Risk Assessment & Alerts
                          </Typography>
                          <List dense>
                            {aiAnalysis.risks.map((risk: string, index: number) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <Warning color="error" />
                                </ListItemIcon>
                                <ListItemText primary={risk} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {aiAnalysis.message}
                    </Typography>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default EnhancedPondAnalytics;
